/*
DOM helper functions
Examples:

ready(() => {
    console.log('DOM is ready');
    const element = find(".selector"); // shortcut for querySelector
    const elements = element.findAll(".another .selector"); // shortcut for querySelectorAll on an element
    element.on('click', (e) => { // shortcut for addEventListener
        e.preventDefault();
        console.log('clicked', e);
    });
    on('click', '.list-item', (e) => { // event delegation for possibly live changing elements
        console.log('clicked on list item', e.target);
    });
    element.hide(); // hide element
    element.show(); // show element
    const newElement = create('<div class="new-element">Hello World</div>'); // create element from HTML string
    document.body.appendChild(newElement); // append to body
    console.log(formatTime(3661)); // format seconds to H:MM:SS

    element.slideUp(500); // slide up / hide element over 500ms
    setTimeout(() => element.slideDown(500), 1000); // slide down / show element after 1s over 500ms

    const response = await request('/api/data', 'POST', {key: 'value'}, 'json');
    console.log('API response:', response);
});


 */
// DOMContentLoaded
function ready(fn) {
    if (document.readyState != 'loading') {
        fn();
    } else {
        document.addEventListener('DOMContentLoaded', fn);
    }
}


/* DOM helpers. globalThis = window */
globalThis.find = document.querySelector.bind(document);
globalThis.findAll = document.querySelectorAll.bind(document);

Element.prototype.find = Element.prototype.querySelector;
Element.prototype.findAll = Element.prototype.querySelectorAll;

// shortcut for addEventListener: find('.movie-info').on('click', (e) => { ....
Element.prototype.on = Element.prototype.addEventListener;
document.on = document.addEventListener;

function delegate(eventType, selector, handler) {
    document.addEventListener(eventType, function(event) {
        const targets = document.querySelectorAll(selector);
        const target = event.target;

        for (let i = 0; i < targets.length; i++) {
            let el = target;
            while (el && el !== this) {
                if (el === targets[i]) {
                    handler.call(el, event);
                    return;
                }
                el = el.parentNode;
            }
        }
    }, true);
}

// event handling for possibly live changing elements: on('.list-item', 'click', (e) => { ...
globalThis.on = delegate;

Element.prototype.show = function () {
    this.style.display = this.dataset._display || 'block';
}

Element.prototype.hide = function () {
    this.dataset._display = window.getComputedStyle(this, null).display; // remember original display
    this.style.display = 'none';
}

// create DOM element(s) from HTML string
globalThis.create = function (string) {
    const template = document.createElement('template');
    template.innerHTML = string.trim();
    return template.content.firstChild;
}

// Slide down / reveal an element
Element.prototype.slideDown = function (duration = 700) {
    // Clear any running animations
    if (this._slideAnimationTimeout) {
        clearTimeout(this._slideAnimationTimeout);
        this._slideAnimationTimeout = null;
    }

    this.style.display = this.dataset._display || 'block';
    this.style.height = '0px';
    this.style.overflow = 'hidden';
    this.style.transition = `height ${duration}ms cubic-bezier(0.25, 1, 0.5, 1)`;

    const height = this.scrollHeight + 'px';
    requestAnimationFrame(() => {
        this.style.height = height;
        this._slideAnimationTimeout = setTimeout(() => {
            this.style.height = '';
            this.style.overflow = '';
            this.style.transition = '';
            this._slideAnimationTimeout = null;
        }, duration);
    });
}

// Slide up / hide an element
Element.prototype.slideUp = function (duration = 700) {
    // Clear any running animations
    if (this._slideAnimationTimeout) {
        clearTimeout(this._slideAnimationTimeout);
        this._slideAnimationTimeout = null;
    }

    this.style.overflow = 'hidden';
    this.style.transition = `height ${duration}ms cubic-bezier(0.25, 1, 0.5, 1)`;

    const height = this.scrollHeight + 'px';
    this.style.height = height; // set to current height first
    requestAnimationFrame(() => {
        this.style.height = '0px'; // then animate to 0
        this._slideAnimationTimeout = setTimeout(() => {
            this.style.display = 'none'; // hide after animation
            this.style.height = '';
            this.style.overflow = '';
            this.style.transition = '';
            this._slideAnimationTimeout = null;
        }, duration);
    });
}

/**
 * HTTP fetch request wrapper
 * @param {string} url - The endpoint URL
 * @param {object} options - Request options
 * @param {string} options.method - HTTP method (GET, POST, etc.)
 * @param {object|array} options.data - Data to send (object or array of key-value pairs)
 * @param {string} options.responseFormat - Response format ("text", "json", or "raw" for raw response)
 * @param {object} options.headers - Additional headers
 * @param {string} options.contentType - Content type ("form" or "json")
 * @param {boolean} options.withCredentials - Include credentials for cross-site requests
 * @param {number} options.timeout - Request timeout in milliseconds
 * @returns {Promise<{data: any, status: number, error: string}>} Standardized response object
 */
async function request(url, options = {}) {
    const {
        method = "GET",
        data = {},
        responseFormat = "json",
        headers = {},
        contentType = "form",
        withCredentials = true,
        timeout = 5000
    } = options;

    const responseObj = {
        data: null,
        status: 0,
        error: "",
        ok: false
    };

    try {
        // AbortController with timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        const fetchConfig = {
            method: method,
            headers: {
                'Content-Type': contentType === "json"
                    ? 'application/json'
                    : 'application/x-www-form-urlencoded',
                ...headers
            },
            credentials: withCredentials ? 'include' : 'same-origin',
            signal: controller.signal // Add the abort signal
        };

        // handle GET parameters
        let requestUrl = url;
        if (method === "GET" && Object.keys(data).length > 0) {
            requestUrl += (url.includes('?') ? '&' : '?') + new URLSearchParams(data).toString();
        }
        // handle request body for non-GET methods
        else if (method !== "GET") {
            fetchConfig.body = contentType === "json"
                ? JSON.stringify(data)
                : new URLSearchParams(data).toString();
        }

        // fetch with abort capability
        const response = await fetch(requestUrl, fetchConfig);

        clearTimeout(timeoutId);

        // set status regardless of success or failure
        responseObj.status = response.status;

        // process response data based on format
        if (responseFormat === "raw") {
            responseObj.data = response;
        } else if (responseFormat === "json") {
            try {
                responseObj.data = await response.json();
            } catch (err) {
                responseObj.data = null;
                responseObj.statusMessage = "Failed to parse JSON response";
            }
        } else {
            responseObj.data = await response.text();
        }

        if (!response.ok) {
            responseObj.statusMessage = `HTTP error ${response.status}: ${response.statusText}`;
        }

        responseObj.ok = response.ok;

    } catch (error) {
        if (error.name === 'AbortError') {
            responseObj.statusMessage = `Request timeout after ${timeout}ms`;
        } else {
            responseObj.statusMessage = error.message || "Request failed";
        }
        responseObj.ok = false;
        console.error(`Request failed for ${url}:`, error);
    }

    return responseObj;
}



// Cookies

/**
 * Sets a cookie with the given name, value, and options
 * @param {string} name - The cookie name
 * @param {string} value - The cookie value
 * @param {Object} options - Cookie options
 */
function setCookie(name, value, options = {}) {
    // Default options that can be overridden
    const defaultOptions = {
        days: null,
        path: '/',
        secure: location.protocol === 'https:',
        sameSite: 'Lax'
    };

    // Merge options
    const cookieOptions = { ...defaultOptions, ...options };

    // Build the cookie string
    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value || '')}`;

    // Add expiration if days provided
    if (cookieOptions.days !== null) {
        const date = new Date();
        date.setTime(date.getTime() + (cookieOptions.days * 24 * 60 * 60 * 1000));
        cookieString += `; expires=${date.toUTCString()}`;
    }

    // Add other cookie attributes
    cookieString += `; path=${cookieOptions.path}`;
    if (cookieOptions.secure) cookieString += '; secure';
    if (cookieOptions.sameSite) cookieString += `; sameSite=${cookieOptions.sameSite}`;
    if (cookieOptions.domain) cookieString += `; domain=${cookieOptions.domain}`;

    // Set the cookie
    document.cookie = cookieString;
}

/**
 * Gets a cookie value by name
 * @param {string} name - The cookie name
 * @returns {string|null} The cookie value or null if not found
 */
function getCookie(name) {
    const nameEQ = `${encodeURIComponent(name)}=`;
    const cookies = document.cookie.split(';');

    for (const cookie of cookies) {
        const trimmedCookie = cookie.trim();
        if (trimmedCookie.indexOf(nameEQ) === 0) {
            return decodeURIComponent(trimmedCookie.substring(nameEQ.length));
        }
    }
    return null;
}

/**
 * Gets all cookies with a specific prefix
 * @param {string} prefix - The cookie name prefix
 * @returns {Object} Object with cookie names and values
 */
function getCookiesWithPrefix(prefix) {
    const cookies = document.cookie.split(';');
    const result = {};
    const encodedPrefix = encodeURIComponent(prefix);

    for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name.startsWith(encodedPrefix)) {
            result[decodeURIComponent(name)] = decodeURIComponent(value);
        }
    }

    return result;
}

/**
 * Deletes all cookies with a specific prefix
 * @param {string} prefix - The cookie name prefix
 * @param {Object} options - Cookie deletion options
 */
function deleteCookiesWithPrefix(prefix, options = {}) {
    const cookies = getCookiesWithPrefix(prefix);
    const cookieOptions = { days: -1, ...options };

    for (const name in cookies) {
        setCookie(name, '', cookieOptions);
    }
}